import { isValidBIC } from "ibantools";
import { z } from "zod";
import { neverFunc } from "./neverFunc";
import { dateSchema } from "./sharedSchemas";
const UsAbaValidNetworks = {
    ACH: "ACH",
    FEDWIRE: "FEDWIRE",
};
const AchAccountType = {
    CHECKING: "CHECKING",
    SAVINGS: "SAVINGS",
};
const PaymentIdentifierType = {
    swift: "swift",
    swift_bic: "swift_bic",
    iban: "iban",
    au_bsb: "au_bsb",
    au_unit_fund: "au_unit_fund",
    au_payid: "au_payid",
    us_aba: "us_aba",
};
export const identifierSwiftSchema = z
    .object({
    accountNumber: z.string(),
    bic: z.string().refine((val) => isValidBIC(val), {
        message: "Invalid BIC",
    }),
    accountName: z.string().nullable().default(null),
})
    .superRefine((value, ctx) => {
    if (value?.accountNumber === "") {
        ctx.addIssue({
            code: "custom",
            message: "Account number is required for this identifier type",
            path: ["accountNumber"],
        });
    }
});
export const identifierSwiftBicSchema = z.object({
    bic: z.string().refine((val) => isValidBIC(val), {
        message: "Invalid BIC",
    }),
});
export const identifierIbanSchema = z.object({
    iban: z.string().nonempty({ message: "Required" }),
});
export const identifierAU_BSBSchema = z
    .object({
    bsb: z.string().regex(/^\d{6}$/, { message: "Must be 6 digits" }),
    accountNumber: z
        .string()
        .regex(/^\d{4,9}$/, { message: "Must be 4 to 9 digits" }),
    accountName: z.string().nullable().default(null),
})
    .superRefine((value, ctx) => {
    if (value?.bsb === "") {
        ctx.addIssue({
            code: "custom",
            message: "BSB is required for this identifier type",
        });
    }
    if (value?.accountNumber === "") {
        ctx.addIssue({
            code: "custom",
            message: "Account number is required for this identifier type",
        });
    }
    if (value?.bsb && !RegExp(/^\d{6}$/).test(value?.bsb)) {
        ctx.addIssue({
            code: "invalid_format",
            message: "Must be 6 digits",
            path: ["bsb"],
            format: "regex",
            pattern: "^\\d{6}$",
        });
    }
    if (value?.accountNumber &&
        !RegExp(/^\d{4,9}$/).test(value?.accountNumber)) {
        ctx.addIssue({
            code: "invalid_format",
            message: "Must be 4 to 9 digits",
            path: ["accountNumber"],
            format: "regex",
            pattern: "^\\d{4,9}$",
        });
    }
});
export const identifierAU_PayIDSchema = z
    .object({
    accountMobile: z.string().nullish(),
    accountEmail: z.email().nullish(),
    accountABN: z.string().nullish(),
    accountOrganisationID: z.string().nullish(),
    accountCustomPayID: z.string().nullish(),
})
    .superRefine((value, ctx) => {
    if (value?.accountABN &&
        value?.accountABN !== "" &&
        !RegExp(/^\d{11}$/).test(value?.accountABN)) {
        ctx.addIssue({
            code: "custom",
            message: "Must be 11 digits",
            path: ["accountABN"],
        });
    }
    if (value?.accountMobile &&
        value?.accountMobile !== "" &&
        !RegExp(/^\d{10}$/).test(value?.accountMobile)) {
        ctx.addIssue({
            code: "custom",
            message: "Must be 10 digits",
            path: ["accountMobile"],
        });
    }
    if (value?.accountEmail === "" &&
        value?.accountMobile === "" &&
        value?.accountABN === "" &&
        value?.accountOrganisationID === "") {
        ctx.addIssue({
            code: "custom",
            message: "At least one NPP identifier must be populated",
            path: [""],
        });
    }
});
export const identifierAU_UnitFundSchema = z.object({
    accountName: z.string(),
    accountNumber: z.string(),
    holderName: z.string(),
    fundId: z.string().nullish(),
});
export const identifierUS_ABASchema = z
    .object({
    routingNumber: z.string(),
    accountNumber: z.string(),
    accountName: z.string().nullish(),
    accountType: z.enum(AchAccountType),
    validNetworks: z.enum(UsAbaValidNetworks).array(),
})
    .superRefine((value, ctx) => {
    if (value?.routingNumber === "") {
        ctx.addIssue({
            code: "custom",
            message: "Routing number is required for this identifier type",
            path: ["routingNumber"],
        });
    }
    if (value?.accountNumber === "") {
        ctx.addIssue({
            code: "custom",
            message: "Account number is required for this identifier type",
            path: ["accountNumber"],
        });
    }
    if (value && value?.accountType === undefined) {
        ctx.addIssue({
            code: "custom",
            message: "Account type is required for this identifier type",
            path: ["accountType"],
        });
    }
});
export const setPaymentIdentifierSchema = z
    .object({
    institutionName: z.string().optional(),
})
    .and(z.discriminatedUnion("identifierType", [
    z.object({
        identifierType: z.literal(PaymentIdentifierType.iban),
        paymentIdentifierIban: identifierIbanSchema,
    }),
    z.object({
        identifierType: z.literal(PaymentIdentifierType.swift),
        paymentIdentifierSwift: identifierSwiftSchema,
    }),
    z.object({
        identifierType: z.literal(PaymentIdentifierType.swift_bic),
        paymentIdentifierSwiftBic: identifierSwiftBicSchema,
    }),
    z.object({
        identifierType: z.literal(PaymentIdentifierType.au_bsb),
        paymentIdentifierAuBsb: identifierAU_BSBSchema,
    }),
    z.object({
        identifierType: z.literal(PaymentIdentifierType.us_aba),
        paymentIdentifierUsAba: identifierUS_ABASchema,
    }),
    z.object({
        identifierType: z.literal(PaymentIdentifierType.au_payid),
        paymentIdentifierAuPayId: identifierAU_PayIDSchema,
    }),
    z.object({
        identifierType: z.literal(PaymentIdentifierType.au_unit_fund),
        paymentIdentifierAuUnitFund: identifierAU_UnitFundSchema,
    }),
]));
export const paymentIdentifierSchema = z
    .object({
    id: z.string(),
    createdAt: dateSchema,
    updatedAt: dateSchema,
})
    .and(z
    .object({ assetId: z.string() })
    .or(z.object({ beneficiaryAccountId: z.string() }))
    .or(z.object({ fundProductId: z.string() })))
    .and(setPaymentIdentifierSchema);
export function paymentIdentifiersEqual(a, b) {
    const institutionNameEqual = a.institutionName === b.institutionName;
    let exactlyEqual;
    let equivalent;
    switch (a.identifierType) {
        case "au_bsb": {
            if (b.identifierType !== "au_bsb") {
                exactlyEqual = false;
                equivalent = false;
                break;
            }
            equivalent =
                a.paymentIdentifierAuBsb.accountNumber ===
                    b.paymentIdentifierAuBsb.accountNumber &&
                    a.paymentIdentifierAuBsb.bsb === b.paymentIdentifierAuBsb.bsb;
            exactlyEqual =
                equivalent &&
                    a.paymentIdentifierAuBsb.accountName ===
                        b.paymentIdentifierAuBsb.accountName;
            break;
        }
        case "au_payid": {
            if (b.identifierType !== "au_payid") {
                exactlyEqual = false;
                equivalent = false;
                break;
            }
            equivalent =
                (a.paymentIdentifierAuPayId.accountABN ?? null) ===
                    (b.paymentIdentifierAuPayId.accountABN ?? null) &&
                    (a.paymentIdentifierAuPayId.accountCustomPayID ?? null) ===
                        (b.paymentIdentifierAuPayId.accountCustomPayID ?? null) &&
                    (a.paymentIdentifierAuPayId.accountEmail ?? null) ===
                        (b.paymentIdentifierAuPayId.accountEmail ?? null) &&
                    (a.paymentIdentifierAuPayId.accountMobile ?? null) ===
                        (b.paymentIdentifierAuPayId.accountMobile ?? null) &&
                    (a.paymentIdentifierAuPayId.accountOrganisationID ?? null) ===
                        (b.paymentIdentifierAuPayId.accountOrganisationID ?? null);
            exactlyEqual = equivalent;
            break;
        }
        case "au_unit_fund": {
            if (b.identifierType !== "au_unit_fund") {
                exactlyEqual = false;
                equivalent = false;
                break;
            }
            equivalent =
                a.paymentIdentifierAuUnitFund.accountName ===
                    b.paymentIdentifierAuUnitFund.accountName &&
                    a.paymentIdentifierAuUnitFund.accountNumber ===
                        b.paymentIdentifierAuUnitFund.accountNumber &&
                    a.paymentIdentifierAuUnitFund.holderName ===
                        b.paymentIdentifierAuUnitFund.holderName &&
                    (a.paymentIdentifierAuUnitFund.fundId ?? null) ===
                        (b.paymentIdentifierAuUnitFund.fundId ?? null);
            exactlyEqual = equivalent;
            break;
        }
        case "iban": {
            if (b.identifierType !== "iban") {
                exactlyEqual = false;
                equivalent = false;
                break;
            }
            equivalent =
                a.paymentIdentifierIban.iban === b.paymentIdentifierIban.iban;
            exactlyEqual = equivalent;
            break;
        }
        case "swift": {
            if (b.identifierType !== "swift") {
                exactlyEqual = false;
                equivalent = false;
                break;
            }
            equivalent =
                a.paymentIdentifierSwift.accountNumber ===
                    b.paymentIdentifierSwift.accountNumber &&
                    a.paymentIdentifierSwift.bic === b.paymentIdentifierSwift.bic;
            exactlyEqual =
                equivalent &&
                    a.paymentIdentifierSwift.accountName ===
                        b.paymentIdentifierSwift.accountName;
            break;
        }
        case "swift_bic": {
            if (b.identifierType !== "swift_bic") {
                exactlyEqual = false;
                equivalent = false;
                break;
            }
            exactlyEqual =
                a.paymentIdentifierSwiftBic.bic === b.paymentIdentifierSwiftBic.bic;
            // NOTE: Swift BIC is a bank level identifier only and can not be used to match equivalent accounts
            equivalent = false;
            break;
        }
        case "us_aba": {
            if (b.identifierType !== "us_aba") {
                exactlyEqual = false;
                equivalent = false;
                break;
            }
            equivalent =
                a.paymentIdentifierUsAba.accountNumber ===
                    b.paymentIdentifierUsAba.accountNumber &&
                    a.paymentIdentifierUsAba.routingNumber ===
                        b.paymentIdentifierUsAba.routingNumber;
            exactlyEqual =
                equivalent &&
                    (a.paymentIdentifierUsAba.accountType ?? null) ===
                        (b.paymentIdentifierUsAba.accountType ?? null) &&
                    a.paymentIdentifierUsAba.validNetworks.every((vn) => b.paymentIdentifierUsAba.validNetworks.includes(vn)) &&
                    b.paymentIdentifierUsAba.validNetworks.every((vn) => a.paymentIdentifierUsAba.validNetworks.includes(vn)) &&
                    a.paymentIdentifierUsAba.accountName ===
                        b.paymentIdentifierUsAba.accountName;
            break;
        }
        default:
            return neverFunc(a);
    }
    return {
        exactlyEqual: exactlyEqual && institutionNameEqual,
        equivalent,
    };
}
